import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, Flex, Typography, Spin, Button, Form } from '@app/ui';
import { ArrowRightOutlined } from '@ant-design/icons';
import * as R from 'ramda';

import connect from '@app/utils/connect';
import userActions from '@app/actions/user';
import { CENSUS_ACCESS } from '@app/constants/config';
import { User } from '@app/types/api';

import ReloLogo from './images/relo-metrics-logo.svg';

import './styles/SSOLogin.scss';
import LanguageSelector from '@app/components/language-selector/LanguageSelector';

const ROOT_ROUTE = '/';

type Platform = 'edge' | 'census';

type SSOLoginProps = {
    user: User & {
        censusJWT: string;
        isLoggedIn?: boolean;
        isSelectingClient?: boolean;
        clients?: Array<{
            id: number;
            permissions?: string[];
            valuationStatusId?: number | string;
        }>;
        error?:
            | string
            | { message?: string; status?: number; ssoError?: boolean };
    };
    SSOlogin: () => Promise<void>;
    censusSSOlogin: () => Promise<void>;
};

const getPlatformAvailability = (clients: SSOLoginProps['user']['clients']) => {
    if (!clients || clients.length === 0) {
        return { hasCensus: false, hasEdge: false };
    }

    const hasCensus = clients.some(client =>
        client.permissions?.includes(CENSUS_ACCESS),
    );
    const hasEdge = clients.some(
        client =>
            client.valuationStatusId !== 3 && client.valuationStatusId !== '3',
    );

    return { hasCensus, hasEdge };
};

const hasNoClients = (user: SSOLoginProps['user']) => {
    if (user.error) {
        return true;
    }
    if (user.id && (!user.clients || user.clients.length === 0)) {
        return true;
    }
    return false;
};

const SSOLogin = ({ user, SSOlogin, censusSSOlogin }: SSOLoginProps) => {
    const { t } = useTranslation('login');
    const navigate = useNavigate();
    const location = useLocation();
    const env = __PROD_API__ ? 'production' : 'development';

    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isCensusSSOLoading, setIsCensusSSOLoading] = useState(false);
    const [showPlatformSelection, setShowPlatformSelection] = useState(false);
    const [platformError, setPlatformError] = useState<string | null>(null);
    const [selectedPlatform, setSelectedPlatform] = useState<Platform>('edge');

    // Fetch user data on mount to determine platform availability
    useEffect(() => {
        const initializePlatformDetection = async () => {
            if (user.isLoggedIn) {
                setIsInitializing(false);
                return;
            }

            try {
                setIsInitializing(true);
                await SSOlogin();
                setIsInitializing(false);
            } catch (e) {
                console.error('Error fetching user data:', e);
                setPlatformError(
                    'Unable to determine platform availability. Please try again.',
                );
                setIsInitializing(false);
            }
        };

        initializePlatformDetection();
    }, []);

    useEffect(() => {
        if (user?.censusJWT) {
            // Append JWT cookie
            document.cookie = `JWT=${user.censusJWT}; domain=relometrics.com; path=/; Secure; SameSite=Lax`;
            // Local development (localhost doesn't support domain attribute), to debug if we are actually setting the cookie
            document.cookie = `JWT=${user.censusJWT}; path=/; SameSite=Lax`;
        }
    }, [user?.censusJWT]);

    // After user data is loaded, determine which platforms are available
    useEffect(() => {
        if (isInitializing || user.isLoggedIn) {
            return;
        }

        // Check if we have a 401 Unauthorized error - redirect to /login
        if (
            user.error &&
            typeof user.error === 'object' &&
            user.error.status === 401
        ) {
            navigate('/login');
            return;
        }

        if (hasNoClients(user)) {
            const errorMessage =
                typeof user.error === 'string'
                    ? user.error
                    : user.error?.message ||
                      'You do not have a client associated to your account. Please contact support.';
            setPlatformError(errorMessage);
            return;
        }

        if (user.clients && user.clients.length > 0) {
            const { hasCensus, hasEdge } = getPlatformAvailability(
                user.clients,
            );

            // Pre-fetch Census SSO cookie if user has Census access
            const fetchCensusSSO = async () => {
                if (hasCensus) {
                    setIsCensusSSOLoading(true);
                    try {
                        await censusSSOlogin();
                    } catch (e) {
                        console.error('Census SSO pre-fetch error:', e);
                    } finally {
                        setIsCensusSSOLoading(false);
                    }
                }
            };

            if (hasCensus && hasEdge) {
                fetchCensusSSO();
                setShowPlatformSelection(true);
            } else if (hasCensus && !hasEdge) {
                fetchCensusSSO().then(() => {
                    handlePlatformSelect('census');
                });
            } else if (!hasCensus && hasEdge) {
                navigate('/login');
            } else {
                setPlatformError(
                    'No platforms are available for your account. Please contact support.',
                );
            }
        }
    }, [user.clients, user.error, user.id, isInitializing, user.isLoggedIn]);

    const handlePlatformSelect = async (platform: Platform) => {
        setIsLoading(true);
        try {
            if (platform === 'census') {
                // Cookie already set in useEffect via censusSSOlogin(), just redirect
                const censusUrl =
                    env === 'production'
                        ? 'https://census.relometrics.com/sso'
                        : 'https://census.stage.rmdevops.com/sso';
                window.location.href = censusUrl;
            } else if (platform === 'edge') {
                await SSOlogin();
                navigate('/login');
            }
        } catch (e) {
            console.error(`${platform} SSO error:`, e);
            setIsLoading(false);
        }
    };

    const handleContinue = () => {
        handlePlatformSelect(selectedPlatform);
    };

    const handlePlatformClick = (platform: Platform) => {
        setSelectedPlatform(platform);
    };

    const { isLoggedIn } = user;

    // If already logged in, redirect to home
    if (isLoggedIn) {
        const from =
            R.path(['state', 'from', 'pathname'], location) || ROOT_ROUTE;
        return <Navigate to={from as string} />;
    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: 'rgba(0, 159, 223, 1)',
                    colorLink: 'rgba(0, 159, 223, 1)',
                    fontFamily: 'Roboto, pnregular, sans-serif',
                },
                components: {
                    Typography: {
                        fontFamily: 'Roboto',
                        fontSize: 16,
                        colorText: 'rgba(0, 0, 0, 0.65)',
                        fontSizeHeading1: 30,
                        colorTextHeading: 'rgba(0, 0, 0, 0.88)',
                    },
                    Button: {
                        primaryColor: '#fff',
                        colorPrimaryHover: 'rgba(0, 139, 195, 1)',
                    },
                },
            }}
        >
            <Flex
                vertical
                justify="center"
                align="center"
                className="sso-login-container"
            >
                <div className="sso-login-card">
                    <Flex vertical align="center" gap={32}>
                        {/* Logo Section */}
                        <Flex vertical align="center">
                            <a
                                href="https://www.relometrics.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    src={ReloLogo}
                                    alt="Relo Metrics"
                                    className="sso-logo"
                                />
                            </a>
                            <Flex vertical align="center">
                                <Typography.Title
                                    level={1}
                                    className="sso-title"
                                >
                                    {t('welcomeTitle')}
                                </Typography.Title>
                                <Typography.Text className="sso-subtitle">
                                    {t('platformSelection.welcomeSubtitle')}
                                </Typography.Text>
                            </Flex>
                        </Flex>

                        {/* Content Section */}
                        <div className="sso-content">
                            {isInitializing && (
                                <Flex
                                    vertical
                                    align="center"
                                    justify="center"
                                    className="sso-loading-state"
                                >
                                    <Spin size="large" />
                                    <Typography.Text
                                        type="secondary"
                                        style={{ marginTop: 16 }}
                                    >
                                        Loading...
                                    </Typography.Text>
                                </Flex>
                            )}

                            {platformError && (
                                <Flex
                                    vertical
                                    align="center"
                                    justify="center"
                                    className="sso-error-state"
                                >
                                    <Typography.Text
                                        type="danger"
                                        style={{ textAlign: 'center' }}
                                    >
                                        {platformError}
                                    </Typography.Text>
                                </Flex>
                            )}

                            {showPlatformSelection && (
                                <Flex vertical className="platform-form">
                                    <Form
                                        name="platformSelection"
                                        layout="vertical"
                                        autoComplete="off"
                                    >
                                        <Flex
                                            vertical
                                            gap={12}
                                            className="platform-selection-cards"
                                        >
                                            {/* EDGE Card */}
                                            <div
                                                className={`platform-card ${
                                                    selectedPlatform === 'edge'
                                                        ? 'platform-selected'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handlePlatformClick('edge')
                                                }
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={e => {
                                                    if (
                                                        e.key === 'Enter' ||
                                                        e.key === ' '
                                                    ) {
                                                        handlePlatformClick(
                                                            'edge',
                                                        );
                                                    }
                                                }}
                                            >
                                                <Typography.Title
                                                    level={3}
                                                    className="platform-name"
                                                >
                                                    {t(
                                                        'platformSelection.edge',
                                                    )}
                                                </Typography.Title>
                                            </div>

                                            {/* CENSUS Card */}
                                            <div
                                                className={`platform-card ${
                                                    selectedPlatform ===
                                                    'census'
                                                        ? 'platform-selected'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handlePlatformClick(
                                                        'census',
                                                    )
                                                }
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={e => {
                                                    if (
                                                        e.key === 'Enter' ||
                                                        e.key === ' '
                                                    ) {
                                                        handlePlatformClick(
                                                            'census',
                                                        );
                                                    }
                                                }}
                                            >
                                                <Typography.Title
                                                    level={3}
                                                    className="platform-name"
                                                >
                                                    {t(
                                                        'platformSelection.census',
                                                    )}
                                                </Typography.Title>
                                            </div>
                                        </Flex>

                                        <Form.Item className="continue-button-item">
                                            <Button
                                                type="primary"
                                                onClick={handleContinue}
                                                block
                                                loading={isLoading || isCensusSSOLoading}
                                                disabled={isLoading || isCensusSSOLoading}
                                                className="continue-button"
                                            >
                                                {isCensusSSOLoading
                                                    ? t('platformSelection.preparingSession')
                                                    : t('platformSelection.continue')}
                                                {!isCensusSSOLoading && <ArrowRightOutlined />}
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </Flex>
                            )}
                        </div>
                    </Flex>
                    <div className="language-selector-container">
                        <LanguageSelector />
                    </div>
                </div>
            </Flex>
        </ConfigProvider>
    );
};

// Declare the global variable for TypeScript
declare const __PROD_API__: boolean;

export default connect(['user'], userActions)(SSOLogin);
